import { materialsApi } from '@/api/materials.api';
import type { MaterialModel } from '@/types/material.types';
import { useCallback, useEffect, useState } from 'react';

/**
 * Load the material catalog.
 *
 * Deliberately silent on failure — the caller renders the catalog as a hint next to data that is
 * useful on its own (the raw STEP strings), so a failed catalog fetch must not raise a toast over
 * a page whose main content loaded fine.
 */
export const useMaterials = () => {
  const [materials, setMaterials] = useState<MaterialModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMaterials = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setMaterials(await materialsApi.getAll());
    } catch (err) {
      console.error('Failed to load the material catalog:', err);
      setError('materials.catalogLoadFailed');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  return { materials, isLoading, error, refetch: fetchMaterials };
};
